import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/App";
import { CRMLayout } from "@/components/CRMLayout";
import { toast } from "sonner";
import { Loader2, FolderKanban, Github, Plus, RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import NouveauProjet from "@/components/NouveauProjet";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const STAGES = {
  discovery: "Découverte", cadrage: "Cadrage", proposition: "Proposition",
  developpement: "Développement", recette: "Recette", livraison: "Livraison",
};
const STATUS_STYLE = {
  active: "bg-emerald-50 text-emerald-700",
  pending: "bg-amber-50 text-amber-700",
  completed: "bg-slate-100 text-slate-600",
};

// Dernière trace de travail : session IA (Orca, GitHub, agents) ou dernier commit.
const derniereActivite = (p) => [p.last_activity_at, p.last_commit?.date].filter(Boolean).sort().pop() || null;

function depuis(iso) {
  if (!iso) return "—";
  const j = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return j <= 0 ? "aujourd'hui" : j === 1 ? "hier" : `il y a ${j} j`;
}

export default function ProjectsPage() {
  const { token, user } = useAuth();
  const [synchro, setSynchro] = useState(false);
  const [creation, setCreation] = useState(false);
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const charger = () =>
    axios.get(`${API_URL}/api/projects`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setProjects([...data].sort((a, b) =>
        (derniereActivite(b) || "").localeCompare(derniereActivite(a) || ""))))
      .catch(() => toast.error("Impossible de charger les projets"))
      .finally(() => setLoading(false));

  useEffect(() => {
    charger();
    // eslint-disable-next-line
  }, []);

  const synchroniser = async () => {
    setSynchro(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/agent/sync/projets`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(data.crees.length
        ? `${data.crees.length} application(s) devenue(s) projet : ${data.crees.join(", ")}`
        : "Projets à jour avec les applications et GitHub");
      charger();
    } catch {
      toast.error("Synchronisation impossible");
    } finally {
      setSynchro(false);
    }
  };

  return (
    <CRMLayout>
      <div className="p-6 lg:p-8">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Projets</h1>
            <p className="text-slate-500 text-sm mt-0.5">
              Projets clients et applications du serveur, reliés à GitHub et mis à jour par les sessions IA
            </p>
          </div>
          {user?.role === "admin" && (
            <div className="flex flex-wrap gap-2">
            <button onClick={() => setCreation(true)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700">
              <Plus className="h-4 w-4" /> Nouveau projet
            </button>
            <button onClick={synchroniser} disabled={synchro}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50">
              <RefreshCw className={`h-4 w-4 ${synchro ? "animate-spin" : ""}`} /> Synchroniser applications & GitHub
            </button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : projects.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-500">
            <FolderKanban className="w-8 h-8 mx-auto mb-3 text-slate-300" />
            Aucun projet pour le moment.<br />
            <span className="text-xs">Les projets se créent depuis une opportunité gagnée ou l'onboarding client.</span>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-[11px] uppercase tracking-wider text-slate-400">
                  <th className="px-5 py-3 font-bold">Projet</th>
                  <th className="px-5 py-3 font-bold">Dépôt</th>
                  <th className="px-5 py-3 font-bold">Activité</th>
                  <th className="px-5 py-3 font-bold">Étape</th>
                  <th className="px-5 py-3 font-bold">Statut</th>
                </tr>
              </thead>
              <tbody>
                {projects.map((p) => (
                  <tr key={p.id}
                    onClick={() => navigate(`/dashboard/projects/${p.id}`)}
                    className="border-b border-slate-100 last:border-0 hover:bg-slate-50 cursor-pointer">
                    <td className="px-5 py-3.5">
                      <p className="font-semibold text-slate-900">{p.name}</p>
                      <p className="text-xs text-slate-400">{[p.company, p.project_type].filter(Boolean).join(" · ")}</p>
                    </td>
                    <td className="px-5 py-3.5">
                      {p.github_url ? (
                        <a href={p.github_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-indigo-600">
                          <Github className="h-3.5 w-3.5" />{p.github_url.split("/").pop()}
                        </a>
                      ) : <span className="text-xs text-slate-300">—</span>}
                    </td>
                    <td className="px-5 py-3.5 text-xs text-slate-500 whitespace-nowrap" title={p.last_commit?.message || ""}>
                      {depuis(derniereActivite(p))}
                    </td>
                    <td className="px-5 py-3.5">
                      <Badge variant="outline">{STAGES[p.current_stage] || p.current_stage}</Badge>
                    </td>
                    <td className="px-5 py-3.5">
                      <span className={`text-xs font-semibold rounded px-2 py-0.5 ${STATUS_STYLE[p.status] || "bg-slate-100 text-slate-600"}`}>
                        {p.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {creation && <NouveauProjet token={token} onFerme={() => setCreation(false)} />}
    </CRMLayout>
  );
}
